/**
 * SearchInput — search icon + input + clear X
 * Extracted from FilterBar.jsx for reuse across filter configurations.
 */
import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useT } from '../../../lib/i18n/I18nContext';

// TICKET-907: onChange writes into AppDataContext, which re-renders every
// context consumer in the app. Report once typing pauses, not per letter.
const DEBOUNCE_MS = 200;

export default function SearchInput({ value, onChange, placeholder, fullWidth = false }) {
  const { t } = useT();
  const [text, setText] = useState(value || '');
  const timer = useRef(null);
  const lastSent = useRef(value || '');

  // An outside change (Clear all, a deep link) replaces what is typed; our own
  // debounced echo coming back does not.
  useEffect(() => {
    const v = value || '';
    if (v !== lastSent.current) { lastSent.current = v; setText(v); }
  }, [value]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const send = (v) => { clearTimeout(timer.current); lastSent.current = v; onChange(v); };
  const type = (v) => {
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => send(v), DEBOUNCE_MS);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'var(--card)',
        borderRadius: 8,
        // Vertical room comes from the input's own 44px (its tap target), not from
        // padding around an 18px-tall input (mobile overflow audit: SHORT input).
        padding: '0 8px',
        border: '1px solid var(--bdr)',
        minWidth: 120,
        flex: fullWidth ? 'unset' : '0 1 180px',
        width: fullWidth ? '100%' : undefined,
      }}
    >
      <Search size={13} color="var(--tm)" strokeWidth={1.5} />
      <input
        value={text}
        onChange={(e) => type(e.target.value)}
        placeholder={placeholder || t('common.search')}
        style={{
          border: 'none',
          background: 'transparent',
          outline: 'none',
          flex: 1,
          fontSize: 12,
          color: 'var(--t1)',
          minWidth: 0,
          minHeight: 44,
        }}
      />
      {text && (
        <button
          onClick={() => { setText(''); send(''); }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
          }}
        >
          <X size={12} color="var(--tm)" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}
